import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { resolveBrand } from '@/lib/brand'
import { translateApp } from '@/lib/i18n'
import {
	clientSessionQueryOptions,
	instanceBrandingQueryOptions
} from '@/query/query-options'

/**
 * The layout of the organization administrator area.
 *
 * `admin/` is an ORDINARY directory inside the `(private)` group, so `/admin`
 * is a real URL segment. `routing.md` warns that only `(group)` folders and
 * `_pathless` layouts avoid becoming a segment — this area needs its segment,
 * so neither form applies here.
 *
 * The guard runs for the landing page and for every slice MS6, MS9 and MS10
 * add below, because a layout route's `beforeLoad` runs for the whole subtree
 * — which is why it is written here, once, before any of those slices exist.
 *
 * IT HIDES; IT DOES NOT AUTHORIZE. `routing.md` carries the reasoning, and it
 * is worth one line here because the opposite is the easy inference: nothing
 * on this side stops a determined caller, and the refusal belongs to the
 * server regardless of what this file does.
 *
 * `ensureQueryData`, not `fetchQuery`: the session gate one level up has
 * already resolved the session, so the cache is warm and a second network read
 * would be waste.
 *
 * A PLATFORM OPERATOR IS REFUSED HERE TOO, which the inequality does without
 * naming them. It is deliberate rather than incidental: their surface is
 * `/operator/*` on the separate operator artifact, and they are not a
 * super-user of somebody else's organization admin area.
 *
 * The redirect carries no explanation. A "you are not an administrator" page
 * would confirm the area exists; the user lands on their own root and nothing
 * states that anything was denied.
 */
export const Route = createFileRoute('/(private)/(shell)/admin')({
	beforeLoad: async ({ context }) => {
		const session = await context.queryClient.ensureQueryData(
			clientSessionQueryOptions()
		)

		if (session.role !== 'organization-admin') {
			throw redirect({ to: '/' })
		}
	},
	// One title for the whole area rather than one per slice: the tab is
	// naming the area a caller is in, not the specific screen, and every
	// page below inherits it. Outside a component, so the brand name
	// reaches it as a value, read from the cache the root loader warmed,
	// rather than through a hook, and the language
	// comes off the route context the root already resolved — the same
	// source `beforeLoad` guards on above. With no product name published
	// the title is the area's own name and nothing else.
	head: ({ match }) => {
		const { productName } = resolveBrand(
			match.context.queryClient.getQueryData(
				instanceBrandingQueryOptions().queryKey
			)
		)

		return {
			meta: [
				{
					title: productName
						? translateApp(
								match.context.appLanguage,
								'client:admin.document_title',
								{ productName }
							)
						: translateApp(
								match.context.appLanguage,
								'client:admin.document_title_unbranded'
							)
				}
			]
		}
	},
	component: AdminAreaLayout
	// No `staticData.breadcrumb` here: `/admin` itself is a redirect
	// (OPSSTD-7911), never a page a person lands on and looks around from,
	// so it earns no step of its own in the trail — each slice's own crumb
	// (`client-admin-people`, `client-admin-audit`, …) is the whole path.
})

function AdminAreaLayout() {
	return <Outlet />
}
